/**
 * Wizard Navigation Component
 *
 * Displays step indicators at the top of the wizard with visual progress.
 * Shows step numbers, titles, and completion status.
 */

import React from 'react';
import { Check } from 'lucide-react';
import { useWizard } from './Wizard';

export interface WizardNavigationProps {
  className?: string;
  showDescriptions?: boolean;
}

export const WizardNavigation: React.FC<WizardNavigationProps> = ({
  className = '',
  showDescriptions = false,
}) => {
  const { steps, currentStep, goToStep } = useWizard();

  return (
    <nav className={`wizard-navigation ${className}`} aria-label="Progress">
      <ol className="flex items-center justify-between w-full">
        {steps.map((step, index) => {
          const isActive = index === currentStep;
          const isCompleted = index < currentStep;
          const isClickable = index < currentStep;

          return (
            <li
              key={step.id}
              className={`flex items-center ${
                index < steps.length - 1 ? 'flex-1' : ''
              }`}
            >
              {/* Step Indicator */}
              <button
                type="button"
                onClick={() => isClickable && goToStep(index)}
                disabled={!isClickable}
                className={`group flex flex-col items-center ${
                  isClickable ? 'cursor-pointer' : 'cursor-default'
                }`}
                aria-current={isActive ? 'step' : undefined}
              >
                {/* Circle */}
                <div
                  className={`flex items-center justify-center w-10 h-10 rounded-full border-2 transition-all ${
                    isCompleted
                      ? 'bg-primary-600 border-primary-600'
                      : isActive
                      ? 'border-primary-600 bg-white'
                      : 'border-gray-300 bg-white'
                  } ${isClickable ? 'group-hover:border-primary-700' : ''}`}
                >
                  {isCompleted ? (
                    <Check className="w-6 h-6 text-white" />
                  ) : (
                    <span
                      className={`text-sm font-semibold ${
                        isActive ? 'text-primary-600' : 'text-gray-500'
                      }`}
                    >
                      {index + 1}
                    </span>
                  )}
                </div>

                {/* Step Title */}
                <div className="mt-2 text-center">
                  <p
                    className={`text-sm font-medium ${
                      isActive
                        ? 'text-primary-600'
                        : isCompleted
                        ? 'text-gray-900'
                        : 'text-gray-500'
                    }`}
                  >
                    {step.label}
                  </p>
                  {showDescriptions && step.description && (
                    <p className="text-xs text-gray-500 mt-1">
                      {step.description}
                    </p>
                  )}
                </div>
              </button>

              {/* Connector Line */}
              {index < steps.length - 1 && (
                <div
                  className={`flex-1 h-0.5 mx-4 transition-all ${
                    index < currentStep ? 'bg-primary-600' : 'bg-gray-300'
                  }`}
                  aria-hidden="true"
                />
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};
